import { approvalChoiceUserText } from "@/components/assistant/approvalChoiceUserText";
import { AssistantChatEmptyState } from "@/components/assistant/AssistantChatEmptyState";
import { AssistantChatErrorNotice } from "@/components/assistant/AssistantChatErrorNotice";
import type { PendingGuidelinePdf } from "@/components/assistant/AssistantPlanContext";
import { AssistantPendingProposalComposerExtras } from "@/components/assistant/AssistantPendingProposalComposerExtras";
import { AssistantWriteApprovalThreadMessage } from "@/components/assistant/AssistantWriteApprovalThreadMessage";
import {
  contractTypePlanDraftFromProposal,
  draftFingerprint,
  mergeContractTypePlanDraft,
  proposalFingerprint,
  type ContractTypePlanDraft,
} from "@/components/assistant/contractTypeGuidelinesDraft";
import { WRITE_APPROVAL_CHOICE_MESSAGE_METADATA } from "@/components/assistant/hiddenChatUiMessages";
import { patchPendingWriteToolProposalInMessages } from "@/components/assistant/patchPendingWriteToolProposalInMessages";
import { settleWriteToolPartWithOutput } from "@/components/assistant/settleWriteToolPartWithOutput";
import { isActivePendingWriteToolPart } from "@/components/assistant/isActivePendingWriteToolPart";
import { useAssistantPlan } from "@/components/assistant/AssistantPlanContext";
import { AssistantPanelPlanSplit } from "@/components/assistant/AssistantPanelPlanSplit";
import { submitAssistantChatMessage } from "@/components/assistant/submitAssistantChatMessage";
import { useAssistantWriteApprovalHandlers } from "@/components/assistant/useAssistantWriteApprovalHandlers";
import { useSyncAssistantPlan } from "@/components/assistant/useSyncAssistantPlan";
import { findActiveWriteApproval } from "@/components/assistant/findActiveWriteApproval";
import { useAutoContinueTextOnlyWriteProposal } from "@/components/assistant/useAutoContinueTextOnlyWriteProposal";
import { applyWriteApprovalSuccessMessageIfNeeded } from "@/components/assistant/applyWriteApprovalSuccessMessage";
import { isHiddenChatUiMessage } from "@/components/assistant/hiddenChatUiMessages";
import { useResolvedWriteApprovals } from "@/components/assistant/useResolvedWriteApprovals";
import { createWriteToolCallApprovalStreamHandler } from "@/components/assistant/applyWriteToolCallApprovalFromStreamPart";
import { useMergePendingWriteApprovals } from "@/components/assistant/useMergePendingWriteApprovals";
import { useAppendUserChatMessage } from "@/components/assistant/useAppendUserChatMessage";
import { ChatMessageBubble } from "@/components/chat/ChatMessageBubble";
import { MessageComposer } from "@/components/chat/MessageComposer";
import { useContractTypes } from "@/hooks/useSupabase";
import { apiUrl } from "@/lib/api-config";
import { getJwtToken } from "@/lib/auth-client";
import { instructionsExceedLimit } from "@/components/form/InstructionWordLimit";
import {
  insertContractTypeGuidelineDocument,
  listContractTypeGuidelineDocuments,
} from "@/lib/guideline-attachments";
import { uploadKnowledgeDoc } from "@/lib/knowledge-docs";
import { referenceUploadFile } from "@/lib/reference-summary-file";
import type { ContractTypeGuidelineDocumentRecord } from "@/types";
import { dedupeUiMessagesForApiRequest } from "@/components/assistant/dedupeUiMessagesForApiRequest";
import { narrowUiMessagesForMastraThreadChat } from "@/components/assistant/narrowUiMessagesForMastraThreadChat";
import { finalizeWriteApprovalStreamMessages } from "@/components/assistant/finalizeWriteApprovalStreamMessages";
import { isUserRejectedWriteToolOutput } from "@/components/assistant/isUserRejectedWriteToolOutput";
import { resetWriteAssistantThread } from "@/components/assistant/resetWriteAssistantThread";
import { assistantTextForChatDisplay } from "@/components/assistant/stripWriteApprovalProposalTable";
import { mapAssistantChatUserError, sanitizeAssistantChatDisplayText } from "@/lib/assistant-chat-user-errors";
import { cn } from "@/lib/utils";
import type { ChatMessage } from "@/types";
import { IconButton } from "@doculegis/shared";
import { useChat } from "@ai-sdk/react";
import {
  DefaultChatTransport,
  isToolUIPart,
  getToolName,
  type UIMessage,
} from "ai";
import { Loader2, Sparkles, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ContractTypeProposalCard } from "./ContractTypeProposalCard";
import { ContractTypeResultCard } from "./ContractTypeResultCard";
import { mergeUiMessageStream } from "./consume-ui-stream";
import {
  findSuccessfulContractTypeWrite,
  isWriteToolName,
  normalizeProposalInput,
  type ContractTypeToolOutput,
  type ToolCallApprovalPayload,
} from "./types";

type Props = {
  className?: string;
  onClose?: () => void;
  onContractTypeWritten?: (contractTypeId: string) => void;
  headerDragHandlers?: {
    onPointerDown: (e: React.PointerEvent) => void;
    onPointerMove: (e: React.PointerEvent) => void;
    onPointerUp: (e: React.PointerEvent) => void;
    onPointerCancel: (e: React.PointerEvent) => void;
  };
};

const AUTO_CONTINUE_FOR_APPROVAL_PROMPT =
  "Invoke create-contract-type (or update-contract-type) now with the exact values from your last proposal so the UI can show approval.";

const EMPTY_PROMPTS = [
  "Create a standard service agreement contract type for Luxembourg",
  "List existing contract types and suggest a unique short code",
  "Update guidelines for an existing contract type",
] as const;

function messageTextContent(message: UIMessage): string {
  return message.parts
    .filter((part): part is Extract<UIMessage["parts"][number], { type: "text" }> => part.type === "text")
    .map((part) => part.text)
    .join("\n\n")
    .trim();
}

function uiMessageToChatMessage(
  message: UIMessage,
  options?: { hideProposalTable?: boolean }
): ChatMessage | null {
  if (isHiddenChatUiMessage(message)) return null;
  let text = messageTextContent(message);
  if (!text) return null;
  if (message.role === "assistant") {
    text = sanitizeAssistantChatDisplayText(text);
    text = assistantTextForChatDisplay(text, {
      hideProposalTable: options?.hideProposalTable,
    });
    if (!text) return null;
  }
  let createdAt: string | undefined;
  const raw = (message as UIMessage & { createdAt?: string | Date }).createdAt;
  if (typeof raw === "string") createdAt = raw;
  else if (raw instanceof Date) createdAt = raw.toISOString();
  return {
    id: message.id,
    role: message.role as "user" | "assistant",
    parts: [{ type: "text", text }],
    createdAt,
  };
}

function AssistantToolRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-row gap-2.5">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--avatar-assistant-border)] bg-[var(--avatar-assistant-bg)] dark:border-transparent dark:bg-gradient-to-br dark:from-blue-500 dark:to-purple-600">
        <Sparkles className="h-4 w-4 text-[var(--avatar-assistant-fg)] dark:text-white" />
      </div>
      <div className="min-w-0 max-w-[84%] flex-1">{children}</div>
    </div>
  );
}

export function ContractTypeAssistantPanel({
  className,
  onClose,
  onContractTypeWritten,
  headerDragHandlers,
}: Props) {
  const { contractTypes, refresh: refreshContractTypes } = useContractTypes();
  const { resolvedToolCallIds, markResolved, clearResolved } = useResolvedWriteApprovals();
  const [input, setInput] = useState("");
  const [resetConversationLoading, setResetConversationLoading] = useState(false);
  const [approvalByToolCallId, setApprovalByToolCallId] = useState<
    Record<string, ToolCallApprovalPayload>
  >({});
  const [actionLoading, setActionLoading] = useState(false);
  const [planDraft, setPlanDraft] = useState<ContractTypePlanDraft>({
    name: "",
    value: "",
    guidelines: "",
  });
  const [pendingPdfs, setPendingPdfs] = useState<PendingGuidelinePdf[]>([]);
  const [savedGuidelineDocs, setSavedGuidelineDocs] = useState<
    ContractTypeGuidelineDocumentRecord[]
  >([]);
  const [assistantProposalConflict, setAssistantProposalConflict] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastSyncedWriteToolCallIdRef = useRef<string | null>(null);
  const planDirtyRef = useRef(false);
  const foldedDocsRef = useRef<ContractTypeGuidelineDocumentRecord[]>([]);
  const guidelineDocsRequestRef = useRef(0);
  const lastProposalFingerprintRef = useRef("");
  const lastPendingToolCallIdRef = useRef("");
  const pendingAssistantProposalRef = useRef<Record<string, unknown> | null>(null);

  const syncListAfterSuccessfulWrite = useCallback(
    async (messageList: UIMessage[]): Promise<boolean> => {
      const write = findSuccessfulContractTypeWrite(messageList);
      if (!write) return false;
      if (lastSyncedWriteToolCallIdRef.current === write.toolCallId) return true;
      lastSyncedWriteToolCallIdRef.current = write.toolCallId;
      await refreshContractTypes({ background: true });
      onContractTypeWritten?.(write.contractTypeId);
      return true;
    },
    [onContractTypeWritten, refreshContractTypes]
  );

  const onWriteToolCallApprovalStreamPart = useMemo(
    () =>
      createWriteToolCallApprovalStreamHandler({
        isWriteToolName,
        normalizeProposalInput,
        setApprovalByToolCallId,
      }),
    [setApprovalByToolCallId]
  );

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: apiUrl("/api/contract-type-chat"),
        credentials: "include",
        prepareSendMessagesRequest: async ({ body, headers, messages }) => {
          const token = await getJwtToken();
          const authHeaders: Record<string, string> = token
            ? { Authorization: `Bearer ${token}` }
            : {};
          return {
            body: {
              ...(body ?? {}),
              messages: narrowUiMessagesForMastraThreadChat(messages),
            },
            headers: {
              ...(typeof headers === "object" && headers != null && !("append" in headers)
                ? (headers as Record<string, string>)
                : {}),
              ...authHeaders,
            },
          };
        },
      }),
    []
  );

  const { messages, setMessages, sendMessage, status, error, clearError } = useChat({
    transport,
    onData: (dataPart) => {
      onWriteToolCallApprovalStreamPart(dataPart);
    },
    onFinish: ({ message, isError }) => {
      if (!isError) clearError();
      void syncListAfterSuccessfulWrite([...messages, message]);
    },
  });

  const appendUserChatMessage = useAppendUserChatMessage(messages, setMessages);

  const startNewConversation = useCallback(async () => {
    setResetConversationLoading(true);
    try {
      await resetWriteAssistantThread("/api/contract-type-chat");
      setMessages([]);
      setApprovalByToolCallId({});
      clearResolved();
      lastSyncedWriteToolCallIdRef.current = null;
      clearError();
    } finally {
      setResetConversationLoading(false);
    }
  }, [clearError, clearResolved, setMessages]);

  useEffect(() => {
    void syncListAfterSuccessfulWrite(messages);
  }, [messages, syncListAfterSuccessfulWrite]);

  const activePending = useMemo(
    () =>
      findActiveWriteApproval({
        messages,
        approvalByToolCallId,
        isWriteToolName,
        normalizeProposalInput,
        resolvedToolCallIds,
      }),
    [messages, approvalByToolCallId, resolvedToolCallIds]
  );

  const { mergePendingApprovalsFromServer } = useMergePendingWriteApprovals({
    pendingUrl: apiUrl("/api/contract-type-chat/pending"),
    status,
    messages,
    isWriteToolName,
    normalizeProposalInput,
    setApprovalByToolCallId,
    resolvedToolCallIds,
    pendingToolCallId: activePending?.approvalReady ? null : activePending?.toolCallId,
    approvalReady: activePending?.approvalReady,
  });

  const uploadPendingPdfs = useCallback(async (contractTypeId: string, omitDocumentIds: string[] = []) => {
    if (pendingPdfs.length === 0) return;
    for (const p of pendingPdfs) {
      const uploaded = await uploadKnowledgeDoc(
        referenceUploadFile(p.fileName, p.extractedText, p.file, p.summarized),
        "contract-types"
      );
      await insertContractTypeGuidelineDocument({
        contract_type_id: contractTypeId,
        file_path: uploaded.path,
        file_name: uploaded.fileName,
        extracted_text: p.extractedText,
        omit_document_ids: omitDocumentIds,
      });
      setPendingPdfs((prev) => prev.filter((item) => item.key !== p.key));
    }
  }, [pendingPdfs]);

  useEffect(() => {
    if (!activePending) {
      setPlanDraft({
        name: "",
        value: "",
        guidelines: "",
      });
      setPendingPdfs([]);
      setSavedGuidelineDocs([]);
      foldedDocsRef.current = [];
      setAssistantProposalConflict(false);
      planDirtyRef.current = false;
      lastProposalFingerprintRef.current = "";
      lastPendingToolCallIdRef.current = "";
      pendingAssistantProposalRef.current = null;
      return;
    }

    const fp = proposalFingerprint(activePending.proposal);
    if (activePending.toolCallId !== lastPendingToolCallIdRef.current) {
      lastPendingToolCallIdRef.current = activePending.toolCallId;
      lastProposalFingerprintRef.current = fp;
      pendingAssistantProposalRef.current = null;
      setAssistantProposalConflict(false);
      setPlanDraft(contractTypePlanDraftFromProposal(activePending.proposal));
      planDirtyRef.current = false;
      foldedDocsRef.current = [];
      setPendingPdfs([]);
      return;
    }

    if (fp === lastProposalFingerprintRef.current) return;

    if (planDirtyRef.current && fp !== draftFingerprint(planDraft)) {
      pendingAssistantProposalRef.current = activePending.proposal;
      setAssistantProposalConflict(true);
      return;
    }

    lastProposalFingerprintRef.current = fp;
    pendingAssistantProposalRef.current = null;
    setAssistantProposalConflict(false);
    setPlanDraft(contractTypePlanDraftFromProposal(activePending.proposal));
    planDirtyRef.current = false;
  }, [activePending?.toolCallId, activePending?.proposal]);

  useEffect(() => {
    const requestId = ++guidelineDocsRequestRef.current;
    const typeId = activePending?.proposal.id;
    if (typeof typeId !== "string" || !typeId) {
      setSavedGuidelineDocs([]);
      return;
    }
    void listContractTypeGuidelineDocuments(typeId)
      .then((docs) => {
        if (guidelineDocsRequestRef.current === requestId) setSavedGuidelineDocs(docs);
      })
      .catch(() => {
        if (guidelineDocsRequestRef.current === requestId) setSavedGuidelineDocs([]);
      });
  }, [activePending?.proposal.id, activePending?.toolCallId]);

  useEffect(() => {
    if (!activePending?.approvalReady || !activePending.toolCallId) return;
    const merged = mergeContractTypePlanDraft(planDraft);
    const toolCallId = activePending.toolCallId;
    const handle = window.setTimeout(() => {
      lastProposalFingerprintRef.current = draftFingerprint(planDraft);
      setMessages((prev) =>
        patchPendingWriteToolProposalInMessages({
          messages: prev,
          toolCallId,
          isWriteToolName,
          proposalPatch: merged,
        })
      );
      setApprovalByToolCallId((prev) => {
        const row = prev[toolCallId];
        if (!row) return prev;
        return {
          ...prev,
          [toolCallId]: { ...row, args: { ...row.args, ...merged } },
        };
      });
    }, 300);
    return () => window.clearTimeout(handle);
  }, [planDraft, activePending?.approvalReady, activePending?.toolCallId, setMessages]);

  const handlePlanDraftChange = useCallback((draft: ContractTypePlanDraft) => {
    planDirtyRef.current = true;
    setPlanDraft(draft);
  }, []);

  const handleApplyAssistantProposal = useCallback(() => {
    const proposal = pendingAssistantProposalRef.current;
    if (!proposal) return;
    setPlanDraft(contractTypePlanDraftFromProposal(proposal));
    planDirtyRef.current = false;
    setAssistantProposalConflict(false);
    lastProposalFingerprintRef.current = proposalFingerprint(proposal);
    pendingAssistantProposalRef.current = null;
  }, []);

  const handleKeepLocalEdits = useCallback(() => {
    const proposal = pendingAssistantProposalRef.current;
    setAssistantProposalConflict(false);
    pendingAssistantProposalRef.current = null;
    if (proposal) {
      lastProposalFingerprintRef.current = proposalFingerprint(proposal);
    }
  }, []);

  const contractTypePlan = useMemo(
    () => ({
      draft: planDraft,
      onDraftChange: handlePlanDraftChange,
      pendingPdfs,
      onQueuePdf: (payload: { file: File; extractedText: string }) => {
        setPendingPdfs((prev) => [
          ...prev,
          {
            key: crypto.randomUUID(),
            file: payload.file,
            fileName: payload.file.name,
            extractedText: payload.extractedText,
            summarized: false,
          },
        ]);
      },
      onRemovePendingPdf: (key: string) => {
        const removed = pendingPdfs.find((item) => item.key === key);
        if (removed?.replacesSavedId) {
          const savedId = removed.replacesSavedId;
          const folded = foldedDocsRef.current.find((item) => item.id === savedId);
          foldedDocsRef.current = foldedDocsRef.current.filter((item) => item.id !== savedId);
          if (folded) {
            setSavedGuidelineDocs((docs) =>
              docs.some((item) => item.id === folded.id) ? docs : [...docs, folded]
            );
          }
        }
        setPendingPdfs((prev) => prev.filter((item) => item.key !== key));
      },
      savedDocs: savedGuidelineDocs,
      getAccessToken: getJwtToken,
      assistantProposalConflict,
      onApplyAssistantProposal: handleApplyAssistantProposal,
      onKeepLocalEdits: handleKeepLocalEdits,
      onFoldReferencePdfs: () => {
        foldedDocsRef.current = savedGuidelineDocs;
        guidelineDocsRequestRef.current += 1;
        setPendingPdfs([]);
        setSavedGuidelineDocs([]);
      },
    }),
    [
      planDraft,
      handlePlanDraftChange,
      pendingPdfs,
      savedGuidelineDocs,
      assistantProposalConflict,
      handleApplyAssistantProposal,
      handleKeepLocalEdits,
    ]
  );

  useAutoContinueTextOnlyWriteProposal({
    status,
    messages,
    actionLoading,
    activePending,
    continuePrompt: AUTO_CONTINUE_FOR_APPROVAL_PROMPT,
    messageTextContent,
    isWriteToolName,
    resolvedToolCallIds,
    sendMessage,
  });

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, status, activePending?.toolCallId]);

  const clearApproval = useCallback(
    (toolCallId: string) => {
      markResolved(toolCallId);
      setApprovalByToolCallId((prev) => {
        const copy = { ...prev };
        delete copy[toolCallId];
        return copy;
      });
    },
    [markResolved]
  );

  const { open: planOpen, setOpen: setPlanOpen } = useAssistantPlan();

  const postApprovalAction = useCallback(
    async (
      path: "approve" | "decline",
      body: Record<string, unknown>,
      messagesForRequest?: UIMessage[],
      options?: { declineReason?: string }
    ) => {
      const requestMessages = dedupeUiMessagesForApiRequest(messagesForRequest ?? messages);
      const pendingSnapshot = findActiveWriteApproval({
        messages: requestMessages,
        approvalByToolCallId,
        isWriteToolName,
        normalizeProposalInput,
        resolvedToolCallIds,
      });
      setActionLoading(true);
      try {
        const omitDocumentIds =
          path === "approve" ? foldedDocsRef.current.map((doc) => doc.id) : [];
        const existingTypeId =
          typeof pendingSnapshot?.proposal.id === "string" ? pendingSnapshot.proposal.id : "";
        if (path === "approve" && existingTypeId && pendingPdfs.length > 0) {
          await uploadPendingPdfs(existingTypeId, omitDocumentIds);
        }
        const token = await getJwtToken();
        const response = await fetch(apiUrl(`/api/contract-type-chat/${path}`), {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            ...body,
            messages: requestMessages,
            ...(omitDocumentIds.length > 0 ? { omit_document_ids: omitDocumentIds } : {}),
          }),
        });
        const settledToolCallId = typeof body.toolCallId === "string" ? body.toolCallId : "";
        const contentType = response.headers.get("content-type") ?? "";
        let nextMessages = requestMessages;

        if (contentType.includes("application/json")) {
          if (!response.ok) {
            const text = await response.text();
            throw new Error(text || `Request failed (${response.status})`);
          }
          const data = (await response.json()) as {
            directWrite?: boolean;
            contractType?: { id: string; name: string; value: string; guidelines?: string | null };
          };
          if (path === "approve" && data.directWrite && data.contractType) {
            nextMessages = settleWriteToolPartWithOutput({
              messages: requestMessages,
              toolCallId: settledToolCallId,
              isWriteToolName,
              output: { success: true, contractType: data.contractType },
            });
            setMessages(nextMessages);
          }
        } else {
          nextMessages = await mergeUiMessageStream(
            response,
            requestMessages,
            setMessages,
            { onStreamPart: onWriteToolCallApprovalStreamPart }
          );
          const declineReason =
            options?.declineReason ??
            (typeof body.reason === "string" ? body.reason : undefined);
          nextMessages = finalizeWriteApprovalStreamMessages({
            messages: nextMessages,
            toolCallId: settledToolCallId,
            isWriteToolName,
            path,
            declineReason: path === "decline" ? declineReason : undefined,
          });
          setMessages(nextMessages);
          await mergePendingApprovalsFromServer();
        }

        clearError();
        if (path === "approve") {
          const synced = await syncListAfterSuccessfulWrite(nextMessages);
          if (!synced) {
            await refreshContractTypes({ background: true });
          }
          const write = findSuccessfulContractTypeWrite(nextMessages);
          const toolCallId = typeof body.toolCallId === "string" ? body.toolCallId : "";
          if (write?.toolCallId === settledToolCallId) {
            foldedDocsRef.current = [];
          }
          if (
            write?.contractTypeId &&
            write.toolCallId === settledToolCallId &&
            !existingTypeId
          ) {
            await uploadPendingPdfs(write.contractTypeId);
          }
          const displayName =
            write?.name ??
            (typeof pendingSnapshot?.proposal.name === "string"
              ? pendingSnapshot.proposal.name
              : "") ??
            (toolCallId ? approvalByToolCallId[toolCallId]?.args?.name : "") ??
            "";
          applyWriteApprovalSuccessMessageIfNeeded({
            kind: "contract-type",
            mode: pendingSnapshot?.mode ?? "create",
            messages: nextMessages,
            setMessages,
            displayName,
          });
          if (settledToolCallId) {
            clearApproval(settledToolCallId);
          }
        }
      } catch (err: unknown) {
        const raw = err instanceof Error ? err.message : String(err);
        throw new Error(mapAssistantChatUserError(raw));
      } finally {
        setActionLoading(false);
      }
    },
    [
      messages,
      setMessages,
      syncListAfterSuccessfulWrite,
      approvalByToolCallId,
      resolvedToolCallIds,
      refreshContractTypes,
      clearError,
      uploadPendingPdfs,
      clearApproval,
      mergePendingApprovalsFromServer,
      onWriteToolCallApprovalStreamPart,
    ]
  );

  const isStreaming = status === "streaming" || status === "submitted";
  const busy = isStreaming || actionLoading;

  const onViewPlan = useCallback(() => setPlanOpen(true), [setPlanOpen]);

  const { pendingDockActions, revisePendingProposal } = useAssistantWriteApprovalHandlers({
    activePending: activePending
      ? {
          toolCallId: activePending.toolCallId,
          runId: activePending.runId,
          mode: activePending.mode,
          approvalReady: activePending.approvalReady,
        }
      : null,
    busy,
    appendUserChatMessage,
    postApprovalAction,
    clearApproval,
    onViewPlan,
  });

  const instructionsOverLimit = instructionsExceedLimit(planDraft.guidelines);

  const handlePlanCreate = useCallback(() => {
    if (!activePending?.approvalReady) return;
    const merged = mergeContractTypePlanDraft(planDraft);
    const hasMaterial =
      merged.guidelines.length > 0 ||
      pendingPdfs.length > 0 ||
      savedGuidelineDocs.length > 0;
    if (!hasMaterial || instructionsOverLimit) return;

    const userText = approvalChoiceUserText({
      choice: "create",
      mode: activePending.mode === "update" ? "update" : "create",
    });
    const withUser = appendUserChatMessage(userText, WRITE_APPROVAL_CHOICE_MESSAGE_METADATA);
    void postApprovalAction(
      "approve",
      {
        runId: activePending.runId,
        toolCallId: activePending.toolCallId,
        proposalOverride: merged,
      },
      withUser
    );
  }, [
    activePending,
    planDraft,
    pendingPdfs.length,
    savedGuidelineDocs.length,
    instructionsOverLimit,
    appendUserChatMessage,
    postApprovalAction,
  ]);

  useSyncAssistantPlan({
    kind: "contract-type",
    activePending: activePending
      ? {
          mode: activePending.mode,
          proposal: activePending.proposal,
          approvalReady: activePending.approvalReady,
        }
      : null,
    disabled: busy || !activePending?.approvalReady,
    submitDisabled: instructionsOverLimit,
    busy,
    onCreate: handlePlanCreate,
    contractTypePlan: activePending ? contractTypePlan : undefined,
  });

  async function submitMessage() {
    const text = input.trim();
    if (!text) return;
    setInput("");
    await submitAssistantChatMessage({
      text,
      status,
      actionLoading,
      activePending,
      appendUserChatMessage,
      revisePendingProposal,
      sendMessage,
    });
  }

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    await submitMessage();
  }

  function renderToolPart(part: UIMessage["parts"][number]) {
    if (!isToolUIPart(part)) return null;
    const toolName = getToolName(part);
    if (!isWriteToolName(toolName)) return null;

    const mode = toolName.toLowerCase().includes("update") ? "update" : "create";
    const inputData = normalizeProposalInput(part.input);
    const approval = approvalByToolCallId[part.toolCallId];
    const existing =
      inputData.id != null
        ? contractTypes.find((c) => c.id === inputData.id) ?? null
        : contractTypes.find(
            (c) =>
              c.name.toLowerCase() === (inputData.name ?? "").toLowerCase() ||
              c.value.toLowerCase() === (inputData.value ?? "").toLowerCase()
          ) ?? null;

    if (
      part.state === "input-available" ||
      part.state === "approval-requested" ||
      (part.state === "input-streaming" && part.input)
    ) {
      if (resolvedToolCallIds.has(part.toolCallId)) {
        return null;
      }
      if (isActivePendingWriteToolPart(part, activePending)) {
        return null;
      }
      const proposalForCard =
        approval?.runId && approval.args?.id ? approval.args : inputData;
      const hasProposalFields = Boolean(
        proposalForCard.name?.trim() ||
          proposalForCard.value?.trim() ||
          proposalForCard.guidelines?.trim()
      );
      if (!hasProposalFields && !approval?.runId) {
        return (
          <p className="text-xs text-[#576274] dark:text-gray-400">
            Waiting for approval context…
          </p>
        );
      }
      return (
        <ContractTypeProposalCard
          mode={mode}
          proposal={proposalForCard}
          existing={mode === "update" ? existing : null}
          showActions={false}
        />
      );
    }

    if (part.state === "output-available") {
      const output = (part.output ?? {}) as ContractTypeToolOutput;
      if (isUserRejectedWriteToolOutput(output)) return null;
      return <ContractTypeResultCard mode={mode} output={output} />;
    }

    if (part.state === "output-error") {
      return (
        <ContractTypeResultCard mode={mode} output={{}} errorText={part.errorText} />
      );
    }

    return null;
  }

  const canSubmit = Boolean(input.trim()) && !busy;

  const streamingAssistantText = useMemo(() => {
    if (!isStreaming) return "";
    const last = messages.length > 0 ? messages[messages.length - 1] : undefined;
    if (last?.role !== "assistant") return "";
    return messageTextContent(last);
  }, [isStreaming, messages]);

  const showThinking = isStreaming && !streamingAssistantText && !actionLoading;

  return (
    <AssistantPanelPlanSplit
      className={cn("dark:bg-gradient-to-b dark:from-slate-800 dark:to-slate-900", className)}
      header={
        <div
          className={cn(
            "flex shrink-0 items-center justify-between border-b border-[rgba(15,23,42,0.09)] bg-gradient-to-r from-blue-600/[0.08] to-purple-600/[0.08] px-4 py-3 dark:border-white/10 dark:from-blue-600/20 dark:to-purple-600/20",
            headerDragHandlers && "cursor-grab touch-none active:cursor-grabbing"
          )}
          {...(headerDragHandlers ?? {})}
        >
          <div className="flex min-w-0 items-center gap-2.5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg shadow-blue-500/20">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div className="min-w-0">
              <h3 className="truncate text-sm font-semibold text-[#101828] dark:text-white">
                Contract Type Assistant
              </h3>
              {headerDragHandlers ? (
                <p className="text-xs text-[#576274] dark:text-gray-400">Drag header to move panel</p>
              ) : (
                <p className="text-xs text-[#576274] dark:text-gray-400">
                  {planOpen ? "Reviewing proposal" : "Create or update contract types with approval"}
                </p>
              )}
            </div>
          </div>
          {onClose ? (
            <IconButton
              type="button"
              size="sm"
              onClick={onClose}
              title="Close chat"
              aria-label="Close chat"
            >
              <X className="h-4 w-4" />
            </IconButton>
          ) : null}
        </div>
      }
      messageArea={
        <div
          ref={scrollRef}
          className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-none"
        >
        <div
          className={cn(
            "p-4",
            messages.length === 0 ? "flex min-h-full flex-col" : "space-y-4"
          )}
        >
          {messages.length === 0 ? (
            <AssistantChatEmptyState
              intro="Describe a contract type to create or update. I will show a review proposal—use View Plan to inspect details, request changes in chat, or Create when ready."
              examples={EMPTY_PROMPTS}
              onSelectExample={setInput}
              disabled={busy}
            />
          ) : null}
          {messages.map((message) => {
            const chatMessage = uiMessageToChatMessage(message, {
              hideProposalTable: Boolean(activePending),
            });
            const toolRows =
              message.role === "assistant"
                ? message.parts.flatMap((part, index) => {
                    const toolUi = renderToolPart(part);
                    if (!toolUi) return [];
                    return [
                      <AssistantToolRow key={`${message.id}-tool-${index}`}>
                        {toolUi}
                      </AssistantToolRow>,
                    ];
                  })
                : [];
            if (!chatMessage && toolRows.length === 0) return null;
            return (
              <div key={message.id} className="space-y-3">
                {chatMessage ? <ChatMessageBubble message={chatMessage} /> : null}
                {toolRows}
              </div>
            );
          })}

          {activePending && pendingDockActions ? (
            <AssistantWriteApprovalThreadMessage
              kind="contract-type"
              mode={activePending.mode}
              approvalReady={activePending.approvalReady}
              proposal={activePending.proposal}
              disabled={pendingDockActions?.disabled}
              submitDisabled={instructionsOverLimit}
              onViewPlan={pendingDockActions?.onViewPlan ?? (() => {})}
              onCreate={handlePlanCreate}
            />
          ) : null}

          {showThinking ? (
            <div className="group flex flex-row gap-2.5">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--avatar-assistant-border)] bg-[var(--avatar-assistant-bg)] dark:border-transparent dark:bg-gradient-to-br dark:from-blue-500 dark:to-purple-600">
                <Sparkles className="h-4 w-4 text-[var(--avatar-assistant-fg)] dark:text-white" />
              </div>
              <div className="relative max-w-[84%] rounded-2xl rounded-tl-sm border border-[rgba(15,23,42,0.09)] bg-white px-4 py-3 text-sm text-[#101828] shadow-[var(--shadow-card)] dark:border-white/10 dark:bg-white/10 dark:text-white dark:shadow-none">
                <div className="flex items-center gap-2 text-[#576274] dark:text-gray-300">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Thinking...</span>
                </div>
              </div>
            </div>
          ) : null}

          {error ? (
            <AssistantChatErrorNotice
              error={error}
              onStartNewConversation={startNewConversation}
              startNewConversationLoading={resetConversationLoading}
            />
          ) : null}
        </div>
        </div>
      }
      composer={
        <MessageComposer
          compact
          value={input}
          onChange={setInput}
          onSubmit={handleSend}
          disabled={busy}
          canSubmit={canSubmit}
          placeholder={
            activePending
              ? "Request a change to the proposal…"
              : "Type your message..."
          }
        >
          {activePending ? (
            <AssistantPendingProposalComposerExtras
              kind="contract-type"
              proposal={activePending.proposal}
              disabled={busy}
              onSelectSuggestion={setInput}
            />
          ) : null}
        </MessageComposer>
      }
    />
  );
}
